import { Fragment, useState, type ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { Prescription } from '@joker/contracts'
import { useTRPC, queryClient } from '../trpc'
import { Panel, Loading, ErrorNote, fmtDate } from '../ui'
import { GRADE_LABEL, GradeChip } from './Jobs'

/**
 * Every prescription read for one person, one row each, newest by the date
 * printed on it: the values, the grade the sales guide gives them, and how it
 * reached us. A person can have several — reading glasses and computer
 * glasses, last year's and this year's. The newest decides the grade shown in
 * the lists. Pressing a row opens why it was graded so, what is still to ask,
 * and the hand-set grade, which reruns keep.
 *
 * The same file read twice (mailed again, forwarded) is listed once; files
 * that were not a prescription are counted at the end rather than listed.
 */
export function ScannedPrescriptions({ personId, embedded = false }: { personId: string; embedded?: boolean }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.prescriptions.forPerson.queryOptions({ personId }))
  /* Whether anything can read a file here at all: the reading job reports it.
     A laptop without the AWS session never reads one, and saying "within
     minutes" there would be a promise nothing keeps. */
  const jobs = useQuery({ ...trpc.jobs.overview.queryOptions(), retry: false })
  const readerOff = jobs.data?.find((j) => j.job === 'extract.prescriptions')?.configured === false
  const override = useMutation(
    trpc.prescriptions.override.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }),
  )
  const [open, setOpen] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [grade, setGrade] = useState<string>('')
  const [note, setNote] = useState('')

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="prescriptions" />

  const prescriptions = q.data.filter((p) => p.verdict === 'prescription' && !p.duplicateOf)
  const others = q.data.filter((p) => p.verdict !== 'prescription')
  const deciding = prescriptions.find((p) => p.gradeOverride ?? p.grade) ?? null

  const summary =
    prescriptions.length === 0
      ? 'none read yet'
      : `${prescriptions.length} · the newest decides: ${deciding ? (GRADE_LABEL[deciding.gradeOverride ?? deciding.grade ?? ''] ?? '—') : '—'}`
  const body: ReactNode = (
    <>
      {prescriptions.length === 0 ? (
        <p className="footnote">
          {readerOff ? (
            <>
              Not read here: prescription reading is not set up on this copy of Joker (it needs the
              Bedrock settings, see Jobs). Online, an uploaded or mailed file is read within minutes.
            </>
          ) : (
            <>
              Nothing read yet. An uploaded file, or one that arrives in support@ or order@, is read
              within minutes: the date on it, both eyes and the add, graded against the sales guide.
            </>
          )}
        </p>
      ) : (
        <div className="rx-cards">
          {prescriptions.map((p) => (
            <article key={p.id} className="rx-card">
              <header className="rx-card-head">
                <span className="rx-card-date">
                  {p.prescriptionDate ? fmtDate(p.prescriptionDate) : <span className="muted">No date on it</span>}
                </span>
                {p.label && <span className="pill test">{p.label}</span>}
                {p.expiryDate && (
                  <span className={new Date(p.expiryDate) < new Date() ? 'bad-text' : 'muted'}>
                    {new Date(p.expiryDate) < new Date() ? 'expired' : 'expires'} {fmtDate(p.expiryDate)}
                  </span>
                )}
                <GradeChip grade={p.gradeOverride ?? p.grade} override={p.gradeOverride != null} />
                <span className="muted">
                  received {fmtDate(p.documentDate)} · {p.source === 'upload' ? 'uploaded' : `mailed to ${p.mailbox ?? 'support'}`} ·{' '}
                  <a href={`/api/prescriptions/${p.id}/file`} target="_blank" rel="noopener">
                    {p.filename ?? 'open file'}
                  </a>
                </span>
                <button
                  type="button"
                  className="linklike rx-card-more"
                  aria-expanded={open === p.id}
                  onClick={() => setOpen(open === p.id ? null : p.id)}
                >
                  {open === p.id ? 'Hide details' : 'Details'}
                </button>
              </header>

              {/* Laid out like the Oryx intake form: each eye's sphere, cylinder
                  and axis, then PD and addition below a rule. Read-only. */}
              <div className="rx-grid" role="table" aria-label="Prescription">
                <div className="rx-h" role="columnheader">Eye</div>
                <div className="rx-h" role="columnheader">Sphere</div>
                <div className="rx-h" role="columnheader">Cylinder</div>
                <div className="rx-h" role="columnheader">Axis</div>
                {(
                  [
                    ['OD', p.od],
                    ['OS', p.os],
                  ] as const
                ).map(([eye, v]) => (
                  <Fragment key={eye}>
                    <div className="rx-eye" role="rowheader">{eye}</div>
                    <div className="rx-box">{power(v.sph)}</div>
                    <div className="rx-box">{power(v.cyl)}</div>
                    <div className="rx-box">{v.axis == null ? '—' : v.axis}</div>
                  </Fragment>
                ))}
              </div>
              <div className="rx-foot">
                <label>
                  <span>PD (mm)</span>
                  <div className="rx-box">
                    {p.pd.distance == null ? '—' : p.pd.distance}
                    {p.pd.near != null && <span className="muted"> · near {p.pd.near}</span>}
                  </div>
                </label>
                <label>
                  <span>Addition</span>
                  <div className="rx-box">{p.add == null ? '—' : power(p.add)}</div>
                </label>
              </div>

              <Prescriber p={p.prescriber} />

              {open === p.id && (
                <div className="rx-card-detail">
              {p.patientName && (
                <p className="footnote" style={{ marginTop: 0 }}>
                  Named on it: {p.patientName}
                  {p.patientDateOfBirth ? ` · born ${p.patientDateOfBirth}` : ''}
                  {p.pd.near != null ? ` · near PD ${p.pd.near} mm` : ''}
                </p>
              )}
              {p.gradeReasons.length > 0 && (
                <ul className="rx-reasons">
                  {p.gradeReasons.map((r, i) => (
                    <li key={i}>{r.text}</li>
                  ))}
                </ul>
              )}
              {p.gradeQuestions.length > 0 && (
                <p className="footnote">
                  <strong>Still to ask:</strong> {p.gradeQuestions.join(' · ')}
                </p>
              )}
              {p.gradeOverride && (
                <p className="footnote">
                  Grade set by hand by {p.overrideBy ?? 'someone'} on {fmtDate(p.overrideAt)}
                  {p.overrideNote ? `: ${p.overrideNote}` : ''}.
                </p>
              )}
              <div className="pform-actions">
                {editing === p.id ? (
                  <>
                    <select className="sel" value={grade} onChange={(e) => setGrade(e.target.value)}>
                      <option value="">Grade…</option>
                      {Object.entries(GRADE_LABEL)
                        .filter(([k]) => k !== 'incomplete')
                        .map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                    </select>
                    <input className="sel" placeholder="Why (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
                    <button
                      type="button"
                      className="btn primary sm"
                      disabled={!grade || override.isPending}
                      onClick={() =>
                        override.mutate(
                          { id: p.id, grade: grade as Exclude<Prescription['grade'], 'incomplete' | null>, note: note || null },
                          { onSuccess: () => setEditing(null) },
                        )
                      }
                    >
                      Save
                    </button>
                    <button type="button" className="btn sm" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => {
                        setEditing(p.id)
                        setGrade(p.gradeOverride ?? '')
                        setNote(p.overrideNote ?? '')
                      }}
                    >
                      Set grade by hand…
                    </button>
                    {p.gradeOverride && (
                      <button
                        type="button"
                        className="btn sm ghost"
                        disabled={override.isPending}
                        onClick={() => override.mutate({ id: p.id, grade: null, note: null })}
                      >
                        Back to the rule’s grade
                      </button>
                    )}
                  </>
                )}
                {override.error && <span className="bad-text">{override.error.message}</span>}
              </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {others.length > 0 && (
        <details className="rx-others">
          <summary className="muted">
            Also scanned: {others.length} {others.length === 1 ? 'file' : 'files'} that{' '}
            {others.length === 1 ? 'was' : 'were'} not a prescription
          </summary>
          <ul>
            {others.map((p) => (
              <li key={p.id}>
                <a href={`/api/prescriptions/${p.id}/file`} target="_blank" rel="noopener">
                  {p.filename ?? 'file'}
                </a>{' '}
                <span className="muted">
                  · received {fmtDate(p.documentDate)} ·{' '}
                  {p.verdict === 'not_prescription'
                    ? 'no prescription in it'
                    : p.verdict === 'unreadable'
                      ? 'could not be read'
                      : `reading failed${p.error ? `: ${p.error.slice(0, 80)}` : ''}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )

  /* Inside the Prescription panel the readings sit under a small heading of
     their own; elsewhere they are a panel. A plain wrapper, not a component
     made per render, so a field being typed in keeps its focus. */
  return embedded ? (
    <section className="rx-readings">
      <h4>
        Readings <span className="muted">· {summary}</span>
      </h4>
      {body}
    </section>
  ) : (
    <Panel title="Prescriptions" note={summary}>
      {body}
    </Panel>
  )
}

/** A power in diopters with its sign, as a prescription writes it: +1.25, −0.50, 0.00. */
function power(v: number | null): string {
  if (v == null) return '—'
  if (v === 0) return '0.00'
  return `${v > 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}`
}

/**
 * Who wrote the prescription and where, as printed on it: the doctor with
 * their credentials and licence, then the practice with its address, phone,
 * email and website. Nothing at all when the file named nobody.
 */
function Prescriber({ p }: { p: Prescription['prescriber'] }) {
  if (!p) return null
  const doctor = [p.name ? `Dr. ${p.name}` : null, p.credentials].filter(Boolean).join(', ')
  const pr = p.practice ?? {}
  return (
    <div className="rx-prescriber">
      <div className="k">Prescribed by</div>
      <div className="facts">
        {doctor && (
          <div className="fact">
            <div className="k">Doctor</div>
            <div className="v">
              {doctor}
              {p.license && <span className="muted"> · licence {p.license}</span>}
            </div>
          </div>
        )}
        {pr.name && (
          <div className="fact">
            <div className="k">Practice</div>
            <div className="v">{pr.name}</div>
          </div>
        )}
        {pr.phone && (
          <div className="fact">
            <div className="k">Phone</div>
            <div className="v">
              <a href={`tel:${pr.phone.replace(/[^\d+]/g, '')}`}>{pr.phone}</a>
            </div>
          </div>
        )}
        {pr.email && (
          <div className="fact">
            <div className="k">Email</div>
            <div className="v">
              <a href={`mailto:${pr.email}`}>{pr.email}</a>
            </div>
          </div>
        )}
        {pr.website && (
          <div className="fact">
            <div className="k">Website</div>
            <div className="v">
              <a href={/^https?:/i.test(pr.website) ? pr.website : `https://${pr.website}`} target="_blank" rel="noopener">
                {pr.website}
              </a>
            </div>
          </div>
        )}
        {pr.address && (
          <div className="fact wide">
            <div className="k">Address</div>
            <div className="v">{pr.address}</div>
          </div>
        )}
      </div>
    </div>
  )
}
